import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  blockIp,
  getSecurityOverview,
  getTrafficReport,
  listAuthEvents,
  listBlockedIps,
  listSecurityEvents,
  resolveSecurityEvent,
  unblockIp,
} from '../../lib/adminApi';
import type { AuthEventsFilters, BlockIpInput, SecurityEventsFilters, TrafficScope } from '../../types/admin';
import { adminKeys } from './adminQueryKeys';

export function useSecurityOverview(hours: number) {
  return useQuery({
    queryKey: adminKeys.security.overview(hours),
    queryFn: () => getSecurityOverview(hours),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useSecurityEvents(filters: SecurityEventsFilters) {
  return useQuery({
    queryKey: adminKeys.security.events(filters as Record<string, unknown>),
    queryFn: () => listSecurityEvents(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useResolveSecurityEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => resolveSecurityEvent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.security.all });
    },
  });
}

export function useAuthEvents(filters: AuthEventsFilters) {
  return useQuery({
    queryKey: adminKeys.security.authEvents(filters as Record<string, unknown>),
    queryFn: () => listAuthEvents(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useTrafficReport(scope: TrafficScope | null, hours = 168) {
  return useQuery({
    queryKey: adminKeys.security.traffic((scope ?? {}) as Record<string, unknown>, hours),
    queryFn: () => getTrafficReport(scope as TrafficScope, hours),
    enabled: Boolean(scope),
    staleTime: 60 * 1000,
  });
}

export function useBlockedIps() {
  return useQuery({
    queryKey: adminKeys.security.blockedIps,
    queryFn: listBlockedIps,
    staleTime: 30 * 1000,
  });
}

export function useBlockIp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BlockIpInput) => blockIp(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.security.all });
    },
  });
}

export function useUnblockIp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => unblockIp(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.security.all });
    },
  });
}
